import { Link } from "react-router-dom";
import { PageHeader, CTASection } from "@/components/Shared";
import { MapPin, Plane, ArrowRight, Quote, CheckCircle2, ShieldCheck, ExternalLink } from "lucide-react";

const VALUE_POINTS = [
  "Direct communication with the Registered Migration Agent who personally prepares and manages your application — never outsourced.",
  "Ability to contact your assigned agent as often as you need throughout your case.",
  "Hands-on support in the preparation, checking and lodgement of all visa and nomination paperwork.",
  "End-to-end assistance with skills assessment, state nomination, employer sponsorship and visa lodgement.",
  "Guidance from Registered Migration Agents who have formally studied Australian migration law.",
  "Backed by over 13 years of leadership experience in the international education and migration consulting industry.",
  "Time-sensitive updates whenever immigration policy, occupation lists or invitation rounds change.",
  "Ethical, honest advice — including when a pathway is not viable. Your time and goals are respected.",
];

const FORMS = [
  {
    to: "/pr-assessment/onshore",
    icon: MapPin,
    eyebrow: "I am in Australia",
    title: "Onshore Visa Assessment",
    desc: "For current visa holders in Australia — students (500), graduates (485), temporary workers (482) and others planning their permanent residency pathway from onshore.",
    points: [
      "Comprehensive 13-section profile",
      "Visa, study & employment history",
      "Skills assessment & state nomination planning",
      "Immigration compliance review",
    ],
    cta: "Start onshore assessment",
    accent: "#C43A89",
    featured: true,
  },
  {
    to: "/pr-assessment/offshore",
    icon: Plane,
    eyebrow: "I am outside Australia",
    title: "Offshore Visa Assessment",
    desc: "For applicants currently living overseas — exploring student, skilled, employer sponsored, partner or family pathways into Australia.",
    points: [
      "Focused 4-step profile",
      "Identify suitable visa categories",
      "Points and eligibility overview",
      "Approximately 5 minutes",
    ],
    cta: "Start offshore assessment",
    accent: "#29324A",
  },
];

export default function PRAssessmentHub() {
  return (
    <div data-testid="pr-hub-page">
      {/* Hero */}
      <section className="bg-[#F8F9FA] border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-5 flex items-center gap-3">
                <span className="brand-dot bg-[#C43A89]" />
                Free Visa Assessment
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#29324A]">
                Find out which Australian visa pathway is right for you.
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
                Complete the assessment form below. One of our Registered Migration Agents will personally review your details and recommend the best pathway.
              </p>
            </div>
            <div className="md:col-span-4 hidden md:block">
              <div className="bg-white border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="brand-dot bg-[#C43A89]" />
                  <div className="brand-dot bg-[#1f2638]" />
                  <div className="brand-dot bg-[#C43A89]" />
                </div>
                <div className="text-[10px] font-bold tracking-widest text-[#C43A89] uppercase">Our Migration Team</div>
                <div className="font-display text-xl text-[#29324A] mt-1 leading-tight">Registered Migration Agent &amp; Licensed Immigration Adviser</div>
                <div className="text-[11px] font-bold tracking-widest text-[#29324A] uppercase mt-2">Australia · New Zealand</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form choices */}
      <section data-testid="form-choices-section" className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" />
            Choose your situation
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[#29324A] tracking-tight leading-tight mb-12 max-w-3xl">
            Where are you currently located?
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            {FORMS.map((f, i) => (
              <Link
                key={i}
                to={f.to}
                data-testid={`pr-form-card-${i}`}
                className={`group relative border-2 p-8 md:p-10 transition-all hover:-translate-y-1 ${
                  f.featured ? "border-[#C43A89] bg-gradient-to-br from-white to-[#fef5fa]" : "border-slate-200 bg-white hover:border-[#29324A]"
                }`}
              >
                {f.featured && (
                  <span className="absolute top-0 right-0 bg-[#C43A89] text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5">
                    Recommended
                  </span>
                )}
                <f.icon className="h-10 w-10 mb-6" style={{ color: f.accent }} />
                <div className="text-[10px] font-bold tracking-widest uppercase mb-3" style={{ color: f.accent }}>
                  {f.eyebrow}
                </div>
                <h3 className="font-display text-2xl md:text-3xl text-[#29324A] leading-tight mb-4">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{f.desc}</p>
                <ul className="space-y-2 mb-8">
                  {f.points.map((p, pi) => (
                    <li key={pi} className="text-sm text-slate-700 flex items-start gap-2.5">
                      <span className="brand-dot mt-1.5 shrink-0" style={{ background: f.accent }} />
                      {p}
                    </li>
                  ))}
                </ul>
                <span
                  className="inline-flex items-center gap-2 text-white font-semibold py-3 px-6 group-hover:gap-3 transition-all btn-shimmer"
                  style={{ background: f.accent }}
                >
                  {f.cta} <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-10 bg-[#F3F2F0] border-l-4 border-[#29324A] p-5 text-sm text-slate-700 leading-relaxed max-w-4xl">
            <strong className="text-[#29324A] block mb-1.5">Important note</strong>
            The Department of Home Affairs accepts visa applications directly. However, if you do not feel confident in lodging an application — or if your case is complex — you may engage a Registered Migration Agent, as indicated by the{" "}
            <a href="https://immi.homeaffairs.gov.au/help-support/who-can-help-with-your-application/using-a-migration-agent" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold inline-flex items-center gap-1">
              Department of Home Affairs <ExternalLink className="h-3 w-3" />
            </a>
            .
          </div>
        </div>
      </section>

      {/* Value of Working with StudyNet (Why Us) */}
      <section data-testid="value-section" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-4 flex items-center gap-3">
                <span className="brand-dot bg-[#C43A89]" />
                The value of working with our team
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#29324A] leading-tight">
                Direct access. Honest advice. Ethical practice.
              </h2>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">
                Working with our team means working directly with a Registered Migration Agent regulated by the{" "}
                <a href="https://www.mara.gov.au" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline font-semibold">Office of the Migration Agents Registration Authority (OMARA)</a>.
              </p>
              <div className="mt-7 flex items-center gap-3 text-sm text-[#29324A] bg-white border border-slate-200 px-4 py-3">
                <ShieldCheck className="h-5 w-5 text-[#C43A89]" />
                <div>
                  <div className="font-bold text-[11px] tracking-widest uppercase">MARN 2619415 | IAA Licence No. 202603837</div>
                  <div className="text-xs text-slate-500">Bound by the Migration Agents Code of Conduct</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <ul className="space-y-px bg-slate-200">
                {VALUE_POINTS.map((p, i) => (
                  <li
                    key={i}
                    data-testid={`value-point-${i}`}
                    className="bg-white p-6 flex items-start gap-4 hover:bg-[#F3F2F0] transition-colors"
                  >
                    <CheckCircle2 className="h-5 w-5 text-[#C43A89] mt-0.5 shrink-0" />
                    <span className="text-[15px] text-slate-700 leading-relaxed">{p}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-[#29324A] text-white p-6 relative grain">
                <div className="relative text-sm leading-relaxed">
                  <strong className="text-[#C43A89] block mb-1.5 text-[11px] tracking-widest uppercase">Transparent fees</strong>
                  If you choose to engage me as your Registered Migration Agent after our free assessment, agency fees apply. Department of Home Affairs application charges and management fees depend on the visa you are eligible for — everything will be confirmed in writing before any engagement.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section data-testid="testimonial-section" className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-6 text-center flex items-center justify-center gap-3">
            <span className="brand-dot bg-[#C43A89]" />
            What clients say
          </div>
          <Quote className="h-10 w-10 text-[#C43A89] mx-auto mb-6" />
          <blockquote className="font-display text-2xl md:text-3xl text-[#29324A] leading-snug tracking-tight text-center">
            "Shamima was patient, honest and clear from our very first call. She prepared every document, kept me updated and answered every question. My partner visa came through smoothly. I cannot recommend her highly enough."
          </blockquote>
          <div className="mt-8 text-[11px] font-bold tracking-[0.2em] text-[#29324A] uppercase text-center">
            — Verified client · Sydney
          </div>
        </div>
      </section>

      <CTASection title="Not sure which assessment to choose? Book a free 10-minute call." />
    </div>
  );
}
